import { Form, Card, Badge, Button, Alert, Spinner } from 'react-bootstrap'
import { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'

type AiProvider = 'DIFY' | 'LLM'
type LlmProvider = 'ANTHROPIC' | 'OPENAI' | ''

interface DifySectionProps {
  difyEnabled: boolean
  difyApiKey: string
  difyApiUrl: string
  onDifyEnabledChange: (value: boolean) => void
  onDifyApiKeyChange: (value: string) => void
  onDifyApiUrlChange: (value: string) => void
  difyAppId: string
  onDifyAppIdChange: (value: string) => void
  organizationId: string
  switchLabel?: string
  // When set, the toggle is disabled and this message explains why
  // (e.g. no messaging channel configured yet).
  disabledReason?: string
  // Conversational AI: provider selector (Dify vs Direct LLM)
  aiProvider?: AiProvider
  onAiProviderChange?: (value: AiProvider) => void
  llmProvider?: LlmProvider
  onLlmProviderChange?: (value: LlmProvider) => void
  llmApiKey?: string
  onLlmApiKeyChange?: (value: string) => void
  llmModel?: string
  onLlmModelChange?: (value: string) => void
}

const LLM_MODEL_PLACEHOLDER: Record<Exclude<LlmProvider, ''>, string> = {
  ANTHROPIC: 'claude-sonnet-5',
  OPENAI: 'gpt-5',
}

export const DifySection = ({
  difyEnabled,
  difyApiKey,
  difyApiUrl,
  onDifyEnabledChange,
  onDifyApiKeyChange,
  onDifyApiUrlChange,
  difyAppId,
  onDifyAppIdChange,
  organizationId,
  switchLabel,
  disabledReason,
  aiProvider = 'DIFY',
  onAiProviderChange,
  llmProvider = 'ANTHROPIC',
  onLlmProviderChange,
  llmApiKey = '',
  onLlmApiKeyChange,
  llmModel = '',
  onLlmModelChange,
}: DifySectionProps) => {
  const [testingConnection, setTestingConnection] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState<'success' | 'error' | null>(null)
  const [connectionMessage, setConnectionMessage] = useState('')
  const [showApiKey, setShowApiKey] = useState(false)

  const handleTestConnection = async () => {
    if (!difyApiKey || !difyApiUrl) {
      toast.warning('Please enter API Key and URL')
      return
    }

    setTestingConnection(true)
    try {
      const response = await axios.get(`${difyApiUrl}/parameters`, {
        headers: { Authorization: `Bearer ${difyApiKey}` },
        timeout: 5000,
      })

      setConnectionStatus('success')
      setConnectionMessage(`✅ Connection verified successfully`)
      toast.success('Dify connection successful')
    } catch (error: any) {
      setConnectionStatus('error')
      const errorMsg = error.response?.status === 401
        ? 'Invalid API Key'
        : error.message || 'Connection error'
      setConnectionMessage(`❌ Error: ${errorMsg}`)
      toast.error(`Could not connect to Dify: ${errorMsg}`)
    } finally {
      setTestingConnection(false)
    }
  }

  useEffect(() => {
    if (!difyEnabled) {
      setConnectionStatus(null)
      setConnectionMessage('')
    }
  }, [difyEnabled])

  const providerBadgeLabel = !difyEnabled
    ? 'Disabled'
    : aiProvider === 'LLM'
    ? `Direct LLM${llmProvider ? ` (${llmProvider === 'ANTHROPIC' ? 'Claude' : 'GPT'})` : ''}`
    : 'Dify'

  return (
    <Card className="h-100 shadow-sm border-0">
      <Card.Header className="bg-light border-0 py-3">
        <div className="d-flex align-items-center justify-content-between w-100">
          <div className="d-flex align-items-center gap-2">
            <span className="fs-3">🤖</span>
            <h5 className="fw-bold text-gray-800 mb-0">Conversational AI</h5>
          </div>
          <Badge bg={difyEnabled ? 'success' : 'secondary'}>
            {providerBadgeLabel}
          </Badge>
        </div>
      </Card.Header>
      <Card.Body>
        {disabledReason && (
          <Alert variant="warning" className="mb-3">
            <small>⚠️ {disabledReason}</small>
          </Alert>
        )}
        <Form.Group className="mb-3">
          <Form.Check
            type="switch"
            label={switchLabel || "Enable Conversational AI for this level"}
            checked={difyEnabled}
            onChange={(e) => onDifyEnabledChange(e.target.checked)}
            disabled={!!disabledReason}
            id="dify-enabled-switch"
          />
          <Form.Text className="text-muted d-block mt-2">
            When enabled, agents can use conversational AI (Dify or a direct LLM) to generate automated responses.
          </Form.Text>
        </Form.Group>

        {difyEnabled && (
          <>
            {/* AI Provider selector */}
            <Form.Group className="mb-3">
              <Form.Label>AI Provider</Form.Label>
              <Form.Select
                value={aiProvider}
                onChange={(e) => onAiProviderChange?.(e.target.value as AiProvider)}
              >
                <option value="DIFY">Dify</option>
                <option value="LLM">Direct LLM</option>
              </Form.Select>
              <Form.Text className="text-muted d-block mt-2">
                Choose whether this level uses Dify (engine + external knowledge base) or a direct LLM (Anthropic/OpenAI) with your credentials.
              </Form.Text>
            </Form.Group>

            {aiProvider === 'DIFY' && (
              <>
                {/* API Key */}
                <Form.Group className="mb-3">
                  <Form.Label>
                    API Key
                    <span className="text-danger ms-1">*</span>
                  </Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type={showApiKey ? 'text' : 'password'}
                      value={difyApiKey}
                      onChange={(e) => onDifyApiKeyChange(e.target.value)}
                      placeholder="app-xxxxxxxxxxxxx"
                      className="form-control"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowApiKey(!showApiKey)}
                      title={showApiKey ? 'Hide' : 'Show'}
                    >
                      {showApiKey ? '👁️‍🗨️' : '🔒'}
                    </Button>
                  </div>
                  <Form.Text className="text-muted d-block mt-2">
                    Get your API Key from your{' '}
                    <a href="https://cloud.dify.ai" target="_blank" rel="noopener noreferrer">
                      Dify dashboard
                    </a>
                  </Form.Text>
                </Form.Group>

                {/* API URL */}
                <Form.Group className="mb-3">
                  <Form.Label>API URL</Form.Label>
                  <Form.Control
                    type="url"
                    value={difyApiUrl}
                    onChange={(e) => onDifyApiUrlChange(e.target.value)}
                    placeholder="https://api.dify.ai/v1"
                  />
                  <Form.Text className="text-muted d-block mt-2">
                    Default: https://api.dify.ai/v1
                  </Form.Text>
                </Form.Group>

                {/* App ID */}
                <Form.Group className="mb-3">
                  <Form.Label>App ID</Form.Label>
                  <Form.Control
                    type="text"
                    value={difyAppId}
                    onChange={(e) => onDifyAppIdChange(e.target.value)}
                    placeholder="Application UUID in Dify"
                  />
                  <Form.Text className="text-muted d-block mt-2">
                    Required to route messages to the correct App.
                  </Form.Text>
                </Form.Group>

                {/* Test Connection Button */}
                <div className="mb-3">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleTestConnection}
                    disabled={testingConnection || !difyApiKey}
                    className="me-2"
                  >
                    {testingConnection ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Testing...
                      </>
                    ) : (
                      '💾 Test Connection'
                    )}
                  </Button>
                  <Form.Text className="text-muted">
                    Verify that your API Key is valid
                  </Form.Text>
                </div>

                {/* Connection Status Alert */}
                {connectionStatus && (
                  <Alert
                    variant={connectionStatus === 'success' ? 'success' : 'danger'}
                    className="mb-3"
                    dismissible
                    onClose={() => setConnectionStatus(null)}
                  >
                    <small>{connectionMessage}</small>
                  </Alert>
                )}
              </>
            )}

            {aiProvider === 'LLM' && (
              <>
                {/* LLM Provider */}
                <Form.Group className="mb-3">
                  <Form.Label>LLM Provider</Form.Label>
                  <Form.Select
                    value={llmProvider}
                    onChange={(e) => onLlmProviderChange?.(e.target.value as LlmProvider)}
                  >
                    <option value="ANTHROPIC">Anthropic (Claude)</option>
                    <option value="OPENAI">OpenAI (GPT)</option>
                  </Form.Select>
                </Form.Group>

                {/* LLM API Key */}
                <Form.Group className="mb-3">
                  <Form.Label>
                    API Key
                    <span className="text-danger ms-1">*</span>
                  </Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type={showApiKey ? 'text' : 'password'}
                      value={llmApiKey}
                      onChange={(e) => onLlmApiKeyChange?.(e.target.value)}
                      placeholder={llmProvider === 'OPENAI' ? 'sk-...' : 'sk-ant-...'}
                      className="form-control"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowApiKey(!showApiKey)}
                      title={showApiKey ? 'Hide' : 'Show'}
                    >
                      {showApiKey ? '👁️‍🗨️' : '🔒'}
                    </Button>
                  </div>
                </Form.Group>

                {/* Model */}
                <Form.Group className="mb-3">
                  <Form.Label>Model</Form.Label>
                  <Form.Control
                    type="text"
                    value={llmModel}
                    onChange={(e) => onLlmModelChange?.(e.target.value)}
                    placeholder={llmProvider ? LLM_MODEL_PLACEHOLDER[llmProvider] : ''}
                  />
                  <Form.Text className="text-muted d-block mt-2">
                    e.g.: {llmProvider ? LLM_MODEL_PLACEHOLDER[llmProvider] : 'claude-sonnet-5 / gpt-5'}
                  </Form.Text>
                </Form.Group>
              </>
            )}

            {/* Info Box */}
            <div className="bg-light p-3 rounded border-start border-info border-4">
              <small className="text-muted d-block">
                <strong>💡 Note:</strong> Agents can use their own Conversational AI settings in their profile, overriding this configuration.
              </small>
            </div>
          </>
        )}

        {!difyEnabled && (
          <Alert variant="info" className="mb-0">
            <small>
              ℹ️ Conversational AI is disabled. Enable this option to use intelligent conversations powered by AI (Dify or Direct LLM).
            </small>
          </Alert>
        )}
      </Card.Body>
    </Card>
  )
}
